// Lightweight email domain typo detector. No external deps.
// Only suggests when we're highly confident (Damerau-Levenshtein distance 1
// to a known common domain), never mutates the user's input.
const COMMON_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "outlook.com",
  "yahoo.es",
  "icloud.com",
];

function damerauLevenshtein(a, b) {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  const dp = Array.from({ length: al + 1 }, () => new Array(bl + 1).fill(0));
  for (let i = 0; i <= al; i++) dp[i][0] = i;
  for (let j = 0; j <= bl; j++) dp[0][j] = j;
  for (let i = 1; i <= al; i++) {
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,       // deletion
        dp[i][j - 1] + 1,       // insertion
        dp[i - 1][j - 1] + cost // substitution
      );
      if (
        i > 1 &&
        j > 1 &&
        a[i - 1] === b[j - 2] &&
        a[i - 2] === b[j - 1]
      ) {
        dp[i][j] = Math.min(dp[i][j], dp[i - 2][j - 2] + 1); // transposition
      }
    }
  }
  return dp[al][bl];
}

/**
 * Returns a corrected email (e.g. `ana@gmial.com` → `ana@gmail.com`)
 * or `null` if nothing to suggest. Never modifies the input.
 */
export function suggestEmailFix(value) {
  if (!value || typeof value !== "string") return null;
  const at = value.lastIndexOf("@");
  if (at <= 0 || at === value.length - 1) return null;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1).trim().toLowerCase();
  if (domain.length < 4) return null;
  // Don't suggest if the domain is already known-good
  if (COMMON_DOMAINS.includes(domain)) return null;
  let best = null;
  let bestDist = Infinity;
  for (const d of COMMON_DOMAINS) {
    const dist = damerauLevenshtein(domain, d);
    if (dist < bestDist) {
      bestDist = dist;
      best = d;
    }
  }
  // Suggest only when the typo is 1 edit away (very confident).
  if (best && bestDist === 1) return `${local}@${best}`;
  return null;
}
